<template>
  <div class="quote-wrapper">
    <customer-show-customer-quote-quicklink :customer="customer"></customer-show-customer-quote-quicklink>
    <div class="card mb-4" v-if="customer.id && existing_subscription">
      <div class="card-body">
        <small>Current Subscription</small>
        <h5 v-if="existing_subscription.name">
          {{ existing_subscription.name }} ({{ existing_subscription.billing_period | t }})
        </h5>
        <h5 v-else><em>None</em></h5>
      </div>
    </div>
    <div v-if="!quote">
      <div class="row">
        <div class="col-auto">
          <button class="btn btn-primary" @click="new_quote(false)">Create new quote</button>
        </div>
        <div class="col-auto">
          <button class="btn btn-primary" @click="new_quote(true)">Force upgrade</button>
        </div>
      </div>
    </div>
    <div class="card mb-4">
      <div class="card-body" v-if="quote">
        <div class="alert alert-primary" v-if="quote.is_forced">{{ 'is_force_quote' | t }}</div>
        <div class="alert bg-primary text-white" v-if="is_edit && !quote.viewed_at">
          The quote has not yet been opened by the customer
        </div>
        <div class="alert bg-primary text-white" v-if="is_edit && quote.viewed_at">
          The quote has been viewed by the customer at {{ quote.viewed_at }}
        </div>
        <div class="alert bg-primary text-white" v-if="quote.approved_by && is_edit">
          This quote has been accepted by <b>{{ quote.approved_by }}</b> and set to activate on
          <b>{{ quote.activation_time | date }}</b>
        </div>
        <div class="alert bg-danger text-white" v-if="!can_use_quote_form">
          Quote is accepted by customer and cannot be changed. If you need changes to the quote, contact customer and
          delete current quote to create a new one
        </div>
        <div class="form-text text-muted" v-if="!quote.is_forced">
          Approval is required by customer, an email is sent to the customer's administrative contact (<b>{{
            customer.admin_contact_email
          }}</b
          >) with a link to an approval page. Upgrades will be activated when the customer approves, downgrades will be
          active on next renewal.
        </div>
        <template v-else>
          <div class="form-group">
            <checkbox v-model="fixed_activation_time" disabled="disabled" label="Set activation time"></checkbox>
          </div>
          <div class="form-group" v-if="fixed_activation_time">
            <label class="d-block">Activation Time</label>
            <datetime-picker v-model="quote.activation_time"></datetime-picker>
            <div class="form-text text-muted">
              Date when the quote will be activated, customer will receive notice 1 month before
            </div>
          </div>
        </template>
        <div class="form-group" v-if="quote.activation_time">
          <checkbox
            v-model="quote.notify_immediately"
            label="Send notification immediately (only for forced/preapproved)"
          ></checkbox>
        </div>
        <div class="form-group" v-if="is_edit">
          <div class="btn btn-outline-danger" @click="destroy_quote">Delete quote</div>
        </div>
      </div>
    </div>
    <template v-if="quote && can_use_quote_form">
      <div class="card mrr-card" v-if="existing_subscription && change_in_mrr">
        <div class="card-body">
          <div class="row">
            <div class="col-6">
              <h5>
                <span>{{ 'change_in_mrr' | t }}:</span
                ><span class="text-success" v-if="change_in_mrr > 0"
                  >{{ change_in_mrr | currency }} {{ quote.currency }}</span
                ><span class="text-warning" v-else-if="change_in_mrr &lt; 0"
                  >{{ change_in_mrr | currency }} {{ quote.currency }}</span
                >
              </h5>
            </div>
            <div class="col-6">
              <h5>
                <span>{{ 'change_in_arr' | t }}:</span
                ><span class="text-success" v-if="change_in_mrr > 0"
                  >{{ (change_in_mrr * 12) | currency }} {{ quote.currency }}</span
                ><span class="text-warning" v-else-if="change_in_mrr &lt; 0"
                  >{{ (change_in_mrr * 12) | currency }} {{ quote.currency }}</span
                >
              </h5>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-body">
          <div class="text-left">
            <div class="row">
              <div class="col text-center">
                <div class="btn-group d-flex flex-wrap justify-content-center">
                  <div
                    class="btn"
                    v-for="(plans, name) in plan_groups"
                    @click="plan_group = plans"
                    :class="plan && plan.name == name ? 'btn-primary' : 'btn-outline-primary'"
                  >
                    {{ name }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col text-center">
                <div class="btn-group" v-if="plan_group">
                  <div
                    class="btn"
                    v-for="period in periods"
                    :class="plan == plan_group[period] ? 'btn-primary' : 'btn-outline-primary'"
                    @click="plan = plan_group[period]"
                  >
                    {{ period | t }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card my-4" v-if="can_override_features_and_price && quote.plan_id">
        <div class="card-body">
          <div>
            <h2>{{ 'plan_overwrite' | t }}</h2>
            <div class="row mt-4">
              <div class="col-md-4">
                <h3>{{ 'billing' | t }}</h3>
                <p class="text-muted">{{ 'billing_information' | t }}</p>
              </div>
              <div class="col-md-8">
                <div class="form-group">
                  <label class="mb-0">{{ 'bank_payment_fee' | t }}</label>
                  <div class="input-group">
                    <input
                      class="form-control"
                      v-model.float="quote.bank_payment_fee"
                      type="number"
                      min="0"
                      step="0.01"
                      @keypress="limitInputDecimals"
                    />
                    <div class="input-group-append">
                      <label class="input-group-text">{{ quote.currency }}</label>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="mb-0">{{ 'payment_fee_percentage' | t }}</label>
                  <div class="input-group">
                    <input
                      class="form-control"
                      v-model.float="quote.payment_fee_percentage"
                      type="number"
                      min="0"
                      step="0.01"
                      @keypress="limitInputDecimals"
                    />
                    <div class="input-group-append">
                      <label class="input-group-text">%</label>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="mb-0">{{ 'plan_change_cause' | t }}</label>
                  <div class="input-group">
                    <select class="form-control" v-model="quote.quote_change_cause">
                      <option v-for="(cause, index) in quote_change_causes" :value="cause" :key="index">
                        {{ cause | t }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card my-4" v-if="quote.plan_id && plan">
        <div class="card-body">
          <h3 class="mt-3 pl-2">{{ 'subscription' | t }}</h3>
          <textarea
            class="form-control my-4 text-left"
            v-if="!quote.is_forced"
            v-model="quote.header_text"
            rows="10"
          ></textarea>
          <div class="alert alert-warning" v-else>{{ 'force_quote' | t }}</div>
          <table class="table table-borderless">
            <thead>
              <tr>
                <th>
                  <div class="text-muted">{{ 'description' | t }}</div>
                </th>
                <th class="text-right">
                  <div class="text-muted">{{ 'price' | t }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <h5 class="mb-3">
                    <span>{{ quote.name }}</span
                    ><span class="text-muted pl-2">({{ quote.billing_period | t }})</span>
                  </h5>
                </td>
                <td class="text-right">
                  <h5>
                    <template v-if="can_override_features_and_price">
                      <input
                        class="mr-2"
                        v-model.float="quote.price"
                        type="number"
                        size="10"
                        :min="plan.price"
                        step="0.01"
                        @keypress="limitInputDecimals"
                      />
                    </template>
                    <template v-else
                      ><span>{{ quote.price | currency }}</span
                      ><span class="pl-1"
                        >{{ quote.currency | t }} / {{ quote.billing_period.replace('ly', '') | t | titlecase }}</span
                      ></template
                    >
                  </h5>
                </td>
              </tr>
              <tr>
                <td colspan="2">
                  <ul class="feature-list">
                    <li>
                      <input
                        class="mr-2"
                        v-model.number="included_locked_shipments"
                        type="number"
                        min="0"
                        step="1"
                        size="5"
                      /><span>Locked {{ 'periodly_shipments' | t(quote.usage_period) }} &times;&nbsp;</span
                      ><span>
                        <input
                          class="mr-2"
                          :value="quote.price_locked_shipments ? quote.price_locked_shipments : quote.price_shipments"
                          @change="($event) => (quote.price_locked_shipments = $event.target.value)"
                          type="number"
                          step="0.01"
                          min="0"
                          size="5"
                          @keypress="limitInputDecimals" /></span
                      ><span>{{ quote.currency | t }}</span>
                    </li>
                    <template v-if="can_override_features_and_price">
                      <li>
                        <input
                          class="mr-2"
                          v-model.number="included_shipments"
                          type="number"
                          size="6"
                          :min="in_usage_period(plan.included_shipments)"
                        /><span>{{ 'periodly_shipments' | t(quote.usage_period) | titlecase }} (</span>
                        <input
                          class="mr-2"
                          v-model.number="quote.price_shipments"
                          type="number"
                          size="4"
                          step="0.01"
                          :min="plan.price_shipments"
                          @keypress="limitInputDecimals"
                        /><span>{{ quote.currency | t }} {{ 'after_that' | t }})</span>
                      </li>
                      <li>
                        <label v-if="unlimited_orders">Unlimited&nbsp;</label>
                        <input
                          class="mr-2"
                          v-else
                          v-model.number="included_orders"
                          type="number"
                          size="6"
                          :min="in_usage_period(plan.included_orders)"
                        /><span>{{ 'periodly_orders' | t(quote.usage_period) | titlecase }} (</span>
                        <input
                          class="mr-2"
                          v-model.number="quote.price_orders"
                          type="number"
                          size="4"
                          step="0.01"
                          :min="plan.price_orders"
                          @keypress="limitInputDecimals"
                        /><span>{{ quote.currency | t }} {{ 'after_that' | t }})</span><span>(&nbsp;</span>
                        <checkbox v-model="unlimited_orders">unlimited orders</checkbox><span>&nbsp;)</span>
                      </li>
                      <li>
                        <input
                          v-model.number="quote.included_printers"
                          type="number"
                          size="3"
                          :min="plan.included_printers"
                        /><span class="pl-2">{{ 'workstations' | t }}</span>
                      </li>
                      <li>
                        <input
                          v-model.number="quote.included_automations"
                          type="number"
                          size="3"
                          :min="plan.included_automations"
                        /><span class="pl-2">{{ 'automations' | t }}</span>
                      </li>
                      <plans-features-table
                        v-model="feature_scopes"
                        :scopes="available_features"
                        :plan="plan"
                        :is_editing_non_custom_plan="!is_custom_plan"
                      ></plans-features-table>
                    </template>
                    <template v-else>
                      <li>
                        <span v-if="included_shipments == null"
                          ><span>{{  'unlimited' |  }}</span></span
                        ><span v-else
                          ><span>{{ included_shipments }}</span
                          ><span v-if="included_locked_shipments > 0"
                            >&nbsp; + {{ included_locked_shipments }}</span
                          ></span
                        ><span
                          >&nbsp; {{ 'periodly_shipments' | t(quote.usage_period) }} ({{
                            quote.price_shipments | currency
                          }}
                          {{ quote.currency | t }} {{ 'after_that' | t }})</span
                        >
                      </li>
                      <li>
                        {{ included_orders == null ? $t('unlimited') : included_orders }}
                        {{ 'periodly_orders' | t(quote.usage_period)
                        }}<span
                          >&nbsp;({{ quote.price_orders | currency }} {{ quote.currency | t }}
                          {{ 'after_that' | t }})</span
                        >
                      </li>
                      <li>
                        {{ quote.included_printers == null ? $t('unlimited') : quote.included_printers }}
                        {{ 'workstations' | t }}
                      </li>
                      <li>
                        {{ quote.included_automations == null ? $t('unlimited') : quote.included_automations }}
                        {{ 'automations' | t }}
                      </li>
                      <li v-for="feature in feature_scopes">{{ feature | t }}</li>
                    </template>
                  </ul>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr v-if="price_locked_shipments_per_period > 0">
                <th>
                  <h5>Extra shipments per {{ quote.billing_period.replace('ly', '') }}</h5>
                </th>
                <th class="text-right">
                  <h5>
                    {{ price_locked_shipments_per_period | currency }} {{ quote.currency | t }} /
                    {{ quote.billing_period.replace('ly', '') | t | titlecase }}
                  </h5>
                </th>
              </tr>
              <tr v-if="can_override_features_and_price">
                <th>
                  <h5 class="text-success">
                    <span>{{ 'running_subscription_discount' | t }}</span
                    ><span class="text-muted pl-2"
                      ><span>(</span>
                      <input
                        v-model.number="quote.subscription_discount"
                        type="number"
                        size="3"
                        step="0.01"
                        @keypress="limitInputDecimals"
                      /><span>% {{ 'discount' | t | titlecase }})</span></span
                    >
                  </h5>
                </th>
                <th class="text-right text-success">
                  <h5>
                    {{ (base_periodly_price - running_subscription_discounted_periodly_price) | currency }}
                    {{ quote.currency | t }}
                  </h5>
                </th>
              </tr>
              <tr>
                <th>
                  <h5 class="text-success">
                    <span>{{ ('first_' + quote.billing_period.replace('ly', '')) | t }}</span
                    ><span class="text-muted pl-2"
                      ><span>(</span>
                      <input
                        v-model.float="billing_discount_percent"
                        type="number"
                        size="3"
                        step="0.01"
                        min="0"
                        @keypress="limitInputDecimals"
                      /><span>% {{ 'discount' | t | titlecase }})</span></span
                    >
                  </h5>
                </th>
                <th class="text-right text-success">
                  <h5>
                    {{
                      (base_periodly_price - running_subscription_discounted_periodly_price - discounted_periodly_price)
                        | currency
                    }}
                    {{ quote.currency | t }}
                  </h5>
                </th>
              </tr>
            </tfoot>
          </table>
          <div v-if="!quote.is_forced">
            <h3 class="pl-2 mt-5 pt-3">{{ 'startup' | t }}</h3>
            <table class="table table-borderless">
              <thead>
                <tr>
                  <th></th>
                  <th>
                    <div class="text-muted">{{ 'description' | t }}</div>
                  </th>
                  <th class="text-right">
                    <div class="text-muted">{{ 'time_header' | t }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="addon in addon_services">
                  <td>
                    <checkbox v-model="addon.selected"></checkbox>
                  </td>
                  <td>
                    <p>{{ addon.key | t }}</p>
                    <textarea class="form-control" v-if="addon.selected" v-model="addon.description"></textarea>
                  </td>
                  <td class="text-right">
                    <input class="text-right" v-model.number="addon.hours" size="2" /><span class="pl-2">{{
                      'hours' | t
                    }}</span>
                  </td>
                </tr>
                <tr v-for="(service, i) in service_lines">
                  <td></td>
                  <td>
                    <input v-model="service.title" />
                    <textarea class="form-control mt-2" v-model="service.description"></textarea>
                  </td>
                  <td class="text-right">
                    <input class="text-right" v-model.number="service.quantity" size="2" /><span class="pl-2">{{
                      'hours' | t
                    }}</span>
                    <p class="mt-3">
                      <button class="btn btn-danger" @click="service_lines.splice(i, 1)">{{ 'delete' | t }}</button>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td class="text-right">
                    <button
                      class="btn btn-primary"
                      @click="
                        service_lines.push({
                          quantity: 1,
                          key: 'service_purchase',
                          unit_price: hour_price,
                          product_id: consultancy_service_id
                        })
                      "
                    >
                      {{ 'add' | t }}
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th></th>
                  <th>
                    <h5>
                      <span class="text-success">{{ 'services_cost' | t }}</span
                      ><span class="text-muted pl-2"
                        ><span>(</span>
                        <input
                          class="text-right"
                          v-model.float="one_time_discount_percent"
                          size="5"
                          step="0.01"
                          min="0"
                          @keypress="limitInputDecimals"
                        /><span>% {{ 'discount' | t | titlecase }})</span></span
                      >
                    </h5>
                  </th>
                  <th class="text-right">
                    <h5 class="nowrap text-success">{{ total_one_time_cost | currency }} {{ quote.currency | t }}</h5>
                  </th>
                </tr>
              </tfoot>
            </table>
            <hr />
            <h3 class="pl-2 mt-5 pt-3">{{ 'purchases' | t }}</h3>
            <table class="table table-borderless">
              <thead>
                <tr>
                  <th>
                    <div class="text-muted">{{ 'product' | t }}</div>
                  </th>
                  <th class="text-right">
                    <div class="text-muted">{{ 'quantity' | t }}</div>
                  </th>
                  <th class="text-right">
                    <div class="text-muted">{{ 'unit_price' | t }}</div>
                  </th>
                  <th class="text-right">
                    <div class="text-muted">{{ 'price' | t }}</div>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(purchase, i) in purchases">
                  <td>
                    <select class="form-control" v-model="purchase.product">
                      <option v-for="product in products" :value="product">
                        {{ product_description(product).title }} - ({{ product_description(product).description }})
                      </option>
                    </select>
                  </td>
                  <td class="text-right">
                    <input class="text-right" v-model.number="purchase.quantity" size="2" />
                  </td>
                  <td class="text-right">{{ format_product_price(purchase.product, 1) }}</td>
                  <td class="text-right">{{ format_product_price(purchase.product, purchase.quantity) }}</td>
                  <td class="text-right">
                    <button class="btn btn-danger" @click="purchases.splice(i, 1)">{{ 'delete' | t }}</button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th class="text-right">
                    <button class="btn btn-primary" @click="add_purchase">{{ 'add' | t }}</button>
                  </th>
                </tr>
                <tr>
                  <th>
                    <h5>
                      <span class="text-success">{{ 'purchases_cost' | t }}</span>
                    </h5>
                  </th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th class="text-right">
                    <h5 class="nowrap text-success">{{ total_purchases_cost | currency }} {{ quote.currency | t }}</h5>
                  </th>
                </tr>
              </tfoot>
            </table>
            <hr />
            <h3 class="mt-5">{{ 'integrations' | t }}</h3>
            <customer-creation-integrations v-model="integration_lines"></customer-creation-integrations>
          </div>
          <template v-if="customer_id">
            <hr />
            <div class="form-group mt-2 text-right">
              <button
                class="btn btn-primary"
                @click="save"
                :disabled="!quote.plan_id || (!quote.is_forced && quote.approved && is_edit)"
              >
                <span v-if="!quote.is_forced">Send quote to customer</span><span v-else>Save scheduled upgrade</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import Api from '@/store/jsonapi';
import IconPrefix from './icon-prefix.vue';
import Bullet from './bullet.vue';
import moment from 'moment-es6';
import Alerts, { handle } from '@/utils/alerts';
import limitInputDecimals from '@/mixins/limit-input-decimals.js';

export default {
  components: {
    'icon-prefix': IconPrefix,
    bullet: Bullet
  },
  mixins: [limitInputDecimals],
  props: {
    value: {
      type: Object
    },
    customer: {
      type: Object
    },
    clone: {
      type: Boolean,
      default: true
    },
    override_features: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      quote: null,
      plan_group: null,
      quote_change_causes: ['unknown', 'new_customer', 'change_in_volume', 'feature_requirement', 'other'],
      statuses: ['active', 'cancelled', 'trial', 'unbilled', 'quote', 'replaced'],
      periods: ['monthly', 'quarterly', 'yearly'],
      billing_discount_percent: 0,
      one_time_discount_percent: 0,
      webshipper_basic_setup_hours: 2,
      go_live_evaluation_hours: 1,
      integration_lines: [],
      service_lines: [],
      suitable_plans: [],
      include_mandatory_setup: true,
      fixed_activation_time: true,
      selected_billing_period: null,
      addon_services: [],
      all_products: [],
      products: null,
      services: null,
      consultancy_service: null,
      purchases: [],
      unlimited_orders: true
    };
  },
  asyncComputed: {
    suitable_plans: async function () {
      let filter = { currency: this.currency };
      if (!this.quote || this.quote.status == 'quote') {
        filter.generation = 2;
      }
      let { data: plans } = await Api.findAllPages('plans', { filter: filter });
      return plans;
    }
  },
  computed: {
    usage_months() {
      return this.get_period_months(this.quote.usage_period);
    },
    included_shipments: {
      get() {
        if (this.quote && this.quote.included_shipments !== null) {
          return Math.round(this.in_usage_period(this.quote.included_shipments));
        }
        return null;
      },
      set(new_included_shipments) {
        if (new_included_shipments) {
          this.quote.included_shipments = new_included_shipments / this.usage_months;
        } else {
          this.quote.included_shipments = 0;
        }
      }
    },
    included_locked_shipments: {
      get() {
        if (this.quote && this.quote.included_locked_shipments !== null) {
          return Math.round(this.in_usage_period(this.quote.included_locked_shipments));
        }
        return null;
      },
      set(new_included_locked_shipments) {
        if (new_included_locked_shipments) {
          this.quote.included_locked_shipments = new_included_locked_shipments / this.usage_months;

          if (!this.quote.price_locked_shipments) {
            this.quote.price_locked_shipments = this.quote.price_shipments;
          }
        } else {
          this.quote.included_locked_shipments = null;
        }
      }
    },
    included_orders: {
      get() {
        if (this.quote && this.quote.included_orders !== null) {
          return Math.round(this.in_usage_period(this.quote.included_orders));
        }
        return null;
      },
      set(new_included_orders) {
        if (new_included_orders) {
          this.quote.included_orders = new_included_orders / this.usage_months;
        } else {
          this.quote.included_orders = null;
        }
      }
    },
    price_locked_shipments_per_period() {
      if (!this.quote) {
        return 0;
      }

      return (this.included_locked_shipments || 0) * (this.quote.price_locked_shipments || 0);
    },
    hour_price() {
      let price =
        this.consultancy_service &&
        this.consultancy_service.prices &&
        this.consultancy_service.prices.find((price) => price.currency == this.currency);
      return price && price.price;
    },
    customer_quote: {
      get() {
        return this.value;
      },
      set(newQuote) {
        this.value = newQuote;
      }
    },
    is_edit() {
      return this.customer_quote && this.customer_quote.id;
    },
    customer_id() {
      return this.customer && this.customer.id;
    },
    currency() {
      return this.customer && this.customer.currency;
    },
    existing_subscription() {
      return this.customer && this.customer.subscription;
    },
    existing_subscription_mrr() {
      let subscription = this.existing_subscription;

      if (!subscription) return;

      return this.calculate_mrr(subscription);
    },
    new_quote_mrr() {
      let quote = this.quote;

      if (!quote) return;

      return this.calculate_mrr(quote);
    },
    change_in_mrr() {
      return (this.new_quote_mrr || 0) - (this.existing_subscription_mrr || 0);
    },
    current_billing_period() {
      if (this.customer_quote) {
        return this.customer_quote.billing_period;
      }
      return this.existing_subscription && this.customer.subscription.billing_period;
    },
    available_features() {
      return (
        this.suitable_plans &&
        this.suitable_plans.length &&
        this.suitable_plans[0].meta &&
        this.suitable_plans[0].meta.feature_scopes
      );
    },
    // Index plans by name name period so we can group plans and select between periods
    plan_groups() {
      let by_name = _.groupBy(this.suitable_plans, 'name');

      return _.mapValues(by_name, (plans) => _.mapValues(_.groupBy(plans, 'billing_period'), (plans) => plans[0]));
    },
    plan: {
      get() {
        if (!this.quote) {
          return null;
        }

        return _.find(this.suitable_plans, (plan) => plan.id == this.quote.plan_id);
      },
      set(newPlan) {
        if (!newPlan) {
          return;
        }

        if (this.quote.plan_id == newPlan.id) {
          return;
        }

        this.quote.plan_id = newPlan.id;

        this.selected_billing_period = newPlan.billing_period;

        this.quote.billing_period = newPlan.billing_period;
        this.quote.usage_period = newPlan.billing_period;
        this.quote.name = newPlan.name;
        this.quote.included_order_channels = newPlan.included_order_channels;
        this.quote.included_users = newPlan.included_users;
        this.quote.included_printers = newPlan.included_printers;
        this.quote.included_carriers = newPlan.included_carriers;
        this.quote.included_automations = newPlan.included_automations;
        this.quote.included_return_portals = newPlan.included_return_portals;
        this.quote.included_shipments = newPlan.included_shipments;
        this.quote.price_shipments = newPlan.price_shipments;
        this.quote.cancellation_periods = newPlan.cancellation_periods || 1;
        this.quote.currency = newPlan.currency;
        this.quote.billing_product_id = newPlan.billing_product_id;
        this.quote.payment_fee_percentage = newPlan.payment_fee_percentage;
        this.quote.quote_change_cause = newPlan.quote_change_cause || 'unknown';
        this.quote.price = newPlan.price;
        this.quote.bank_payment_fee = newPlan.bank_payment_fee;
        this.quote.level_one_support_price = newPlan.level_one_support_price;
        this.quote.level_two_support_price = newPlan.level_two_support_price;
        this.quote.level_three_support_price = newPlan.level_three_support_price;
        this.quote.support_class = newPlan.support_class;
        this.quote.feature_scopes = newPlan.feature_scopes;
        this.quote.price_locked_shipments = newPlan.price_locked_shipments;
      }
    },
    feature_scopes: {
      get() {
        if (this.quote && this.quote.feature_scopes) {
          return this.quote.feature_scopes.split(' ');
        } else {
          return [];
        }
      },
      set(scopes) {
        this.quote.feature_scopes = scopes.join(' ');
      }
    },
    // Subscription price before any discounts, including extra shipments
    base_periodly_price() {
      if (!this.quote) {
        return 0;
      }

      return parseFloat(this.quote.price) + parseFloat(this.price_locked_shipments_per_period);
    },
    discounted_periodly_price() {
      if (!this.billing_discount_percent) {
        return 0;
      }

      return (
        ((this.base_periodly_price - this.running_subscription_discounted_periodly_price) *
          parseFloat(this.billing_discount_percent)) /
        100
      );
    },
    running_subscription_discounted_periodly_price() {
      if (!this.quote.subscription_discount) {
        return 0;
      }

      return (this.base_periodly_price * parseFloat(this.quote.subscription_discount)) / 100;
    },
    total_hours() {
      return (
        _.sum(_.map(this.selected_addon_lines, (l) => parseInt(l.quantity))) +
        _.sum(_.map(this.service_lines, (l) => parseInt(l.quantity)))
      );
    },
    one_time_cost() {
      return this.total_hours * this.hour_price;
    },
    one_time_discount() {
      return this.one_time_cost * (this.one_time_discount_percent / 100);
    },
    total_one_time_cost() {
      return this.one_time_cost - this.one_time_discount;
    },
    total_purchases_cost() {
      return _(this.purchases)
        .map((purchase) => {
          if (purchase && purchase.product && purchase.product.prices && purchase.quantity) {
            return this.product_price(purchase.product).price * purchase.quantity;
          }
          return 0;
        })
        .sum();
    },
    conditions_met() {
      return !!this.plan;
    },
    is_custom_plan() {
      return this.plan && this.plan.name.toLowerCase().includes('enterprise');
    },
    can_override_features_and_price() {
      return this.is_custom_plan || this.override_features || this.can('write_plans');
    },
    consultancy_service_id() {
      return this.consultancy_service && this.consultancy_service.id;
    },
    selected_addon_lines() {
      let selected = _.filter(this.addon_services, 'selected');
      return _.map(selected, (line) => {
        return {
          key: line.key,
          quantity: line.hours,
          description: line.description,
          unit_price: this.hour_price,
          product_id: this.consultancy_service_id
        };
      });
    },
    purchase_lines() {
      return this.purchases.map((purchase) =>
        _.merge(_.omit(purchase, 'product'), {
          product_id: purchase.product && purchase.product.id
        })
      );
    },
    calculated_lines() {
      if (!this.quote) {
        return [];
      }

      let extra_lines = [];
      if (this.discounted_periodly_price > 0) {
        extra_lines.push({
          unit_price: -this.discounted_periodly_price,
          quantity: 1,
          description: this.period_discount_string(),
          key: 'first_period_discount'
        });
      }

      if (this.one_time_discount > 0) {
        extra_lines.push({
          unit_price: -this.one_time_discount,
          quantity: 1,
          description: this.$t('your_one_time_discount'),
          key: 'your_one_time_discount'
        });
      }

      let addon_lines = this.selected_addon_lines;

      return _.filter(
        [...extra_lines, ...addon_lines, ...this.purchase_lines, ...this.service_lines, ...this.integration_lines],
        (l) => l.quantity > 0
      );
    },
    can_use_quote_form() {
      // Do not allow changes to existing quote that is already approved by customer (non forced quotes)
      return !this.is_edit || !this.quote.approved_by || this.quote.is_forced;
    }
  },
  methods: {
    set_addon_services() {
      this.addon_services = [
        {
          key: 'webshipper_basic_setup',
          selected: !this.customer.id && this.customer.onboarding_status != 'migrating',
          hours: 2,
          description:
            this.customer.default_locale == 'da'
              ? 'Dette inkluderer den obligatoriske opsætning i Webshipper. Vores supportmedarbejdere hjælper dig med opsætning din første transportør, ordresystem og printerstation, hvis det er inkluderet i dit abonnoment'
              : 'This includes the mandatory setup with Webshipper. Our onboarding team will assist you with setting up your first carrier, order channel and print station.'
        },
        {
          key: 'go_live_evaluation',
          selected: !this.customer.id && this.customer.onboarding_status != 'migrating',
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? 'Opfølgning og gennemgang af Webshipper-kontoen efter optsætningen'
              : 'A follow-up and walkthrough of our Webshipper account before go live.'
        },
        {
          key: 'scanner_setup',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will assist with setting up scanners with SNAPI-interface.'
        },
        {
          key: 'shipping_rate_configuration',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will setup and configure shipping rates for the following carriers.'
        },
        {
          key: 'paperless_customs_configuration',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will assist with setting up Paperless for the following carriers.'
        },
        {
          key: 'return_portal_configuration',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will assist with setting up x-number of return portal(s). Webshipper will teach how return portals are maintained.'
        },
        {
          key: 'csv_configuration',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will assist with setting up CSV-import. Webshipper needs a copy of a CSV-file that we can use to map fields.'
        },
        {
          key: 'workflow_configuration',
          selected: false,
          hours: 1,
          description:
            this.customer.default_locale == 'da'
              ? ''
              : 'Webshipper will teach how workflows works and can be used. Webshipper will assist with setting up workflows that do the following.'
        },
        {
          key: 'extra_setup_services',
          selected: false,
          hours: 1,
          description: ''
        }
      ];
    },
    get_period_months(period) {
      switch (period) {
        case 'yearly':
          return 12;
        case 'quarterly':
          return 3;
        default:
          return 1;
      }
    },
    in_usage_period(included) {
      return included * (this.usage_months || 1);
    },
    calculate_mrr(subscription) {
      return (
        subscription.price / this.get_period_months(subscription.billing_period) +
        subscription.included_locked_shipments * subscription.price_locked_shipments
      );
    },
    reload_products() {
      this.products = this.all_products.filter(
        (product) =>
          product.product_type == 'product' && product.prices.find((price) => price.currency == this.currency)
      );
      this.services = this.all_products.filter(
        (product) =>
          product.product_type == 'service' && product.prices.find((price) => price.currency == this.currency)
      );
      this.consultancy_service = this.services[0];
    },
    product_price(product) {
      return (
        product &&
        product.prices &&
        (product.prices.find((price) => price.currency == this.customer.currency) || product.prices[0])
      );
    },
    format_product_price(product, quantity) {
      let product_price = this.product_price(product);
      return product_price && quantity
        ? `${(product_price.price * quantity).toFixed(2)} ${product_price.currency}`
        : `- ${this.customer.currency}`;
    },
    product_description(product) {
      return (
        product &&
        product.descriptions &&
        (product.descriptions.find((description) => description.locale == this.customer.default_locale) ||
          product.descriptions[0])
      );
    },
    add_purchase() {
      this.purchases.push({ quantity: 1, key: 'product_purchase' });
    },
    format_float(input) {
      return parseFloat(input.replace(',', '.'));
    },
    new_quote(force = false) {
      this.fixed_activation_time = !!force;
      let header_text = '';

      if (this.customer.default_locale == 'da') {
        header_text = `Hej ${this.customer.admin_contact_name},

Hvis du har spørgsmål så tøv ikke med at kontakte mig på ${this.staff.phone} eller ${this.staff.email}.

Venlig hilsen,
${this.staff ? this.staff.first_name : 'Webshipper'}`;
      } else {
        header_text = `Hi  ${this.customer.admin_contact_name},

If you have any questions, please don’t hesitate to contact me at ${this.staff.phone} or ${this.staff.email}.

All the best,
${this.staff ? this.staff.first_name : 'Webshipper'}
`;
      }

      this.quote = {
        status: 'quote',
        header_text: header_text,
        name: '',
        plan_id: null,
        quote_lines: [],
        is_forced: force,
        notify_immediately: false,
        billing_period: 'monthly',
        usage_period: 'monthly',
        activation_time: force ? moment('2020-11-01 05:00') : null,
        included_order_channels: 0,
        included_users: 0,
        included_printers: 0,
        included_carriers: 0,
        included_automations: 0,
        included_return_portals: 0,
        included_shipments: 0.0,
        included_locked_shipments: 0.0,
        included_orders: null,
        price_shipments: 0,
        price_orders: 0,
        price_order_channels: 0,
        price_users: 0,
        price_printers: 0,
        price_automations: 0,
        price_return_portals: 0,
        price_carriers: 0,
        cancellation_periods: 1,
        currency: this.currency,
        billing_product_id: '',
        payment_fee_percentage: 0,
        quote_change_cause: '',
        subscription_discount: 0,
        price: 0,
        bank_payment_fee: 0,
        level_one_support_price: 0,
        level_two_support_price: 0,
        level_three_support_price: 0,
        support_class: null,
        feature_scopes: null
      };
    },
    toggle_scope(scope) {
      if (this.feature_scopes.includes(scope)) {
        let current_feature_scopes = this.feature_scopes;
        current_feature_scopes.splice(this.feature_scopes.indexOf(scope), 1);
        this.quote.feature_scopes = current_feature_scopes.join(' ');
      } else {
        this.quote.feature_scopes = this.quote.feature_scopes ? this.quote.feature_scopes + ' ' + scope : scope;
      }
    },
    period_discount_string() {
      return this.$t('your_periodly_discount', {
        period: this.quote.billing_period
      });
    },
    period_short(period) {
      return period.slice(0, -2);
    },
    price_string_suffix() {
      return `${this.currency || 'DKK'}`;
    },
    price_string(price) {
      let price_string = price != 0 ? price.toString() : '-';
      return `${price_string} ${this.price_string_suffix()}`;
    },
    periodic_price_string_suffix() {
      return `${this.price_string_suffix()}/${this.$t(this.period_short(this.quote.billing_period))}`;
    },
    periodic_price_string(price) {
      return `${price} ${this.periodic_price_string_suffix()}`;
    },
    hours_string(hours) {
      return `${hours} ${this.$t('hours')}`;
    },
    is_percentage(percentage) {
      return percentage <= 100 && percentage >= 0 && !percentage.toString().includes('-');
    },
    destroy_quote: handle(async function () {
      if (this.is_edit) {
        let { data: customer } = await Api.update('customers', {
          id: this.customer_id,
          quote: null
        });
        this.customer_quote = customer.quote;
      }
      this.quote = null;
    }),
    save: handle(async function () {
      let save_quote = _.cloneDeep(this.quote);

      // Avoid changing accepted quotes, send a new quote instead. Remember to reset viewed at and notified_at as well.
      if (!save_quote.is_forced) {
        save_quote.id = null;
        save_quote.viewed_at = null;
        save_quote.notify_time = null;
      }

      save_quote.quote_lines = this.calculated_lines;
      _.each(save_quote.quote_lines, (quote_line) => (quote_line.id = null));

      save_quote.subscription_discount = save_quote.subscription_discount ? save_quote.subscription_discount : null;

      if (save_quote.is_forced) {
        save_quote.approved_by = `${this.staff.first_name} ${this.staff.last_name} (${this.staff.email})`;
      } else {
        save_quote.approved_by = null;
      }

      if (!this.fixed_activation_time) {
        save_quote.activation_time = null;
      }

      save_quote.customer_id = this.customer_id;
      save_quote.currency = this.customer.currency;
      let { data: customer } = await Api.update('customers', {
        id: this.customer_id,
        quote: save_quote
      });
      this.customer_quote = customer.quote;

      Alerts.success({ title: 'Quote saved', detail: '' });
    }),
    restore_quote_details() {
      // Restore non unlimited included orders
      if (this.quote.included_orders !== null) {
        this.unlimited_orders = false;
      }

      // Restore first period discount
      let first_period_discount_line = _.find(this.quote.quote_lines, (line) => line.key == 'first_period_discount');
      if (first_period_discount_line) {
        const base_period_after_discount =
          this.base_periodly_price - (this.base_periodly_price * this.quote.subscription_discount) / 100;
        this.billing_discount_percent =
          -1 * ((first_period_discount_line.unit_price / base_period_after_discount) * 100).toFixed(1);
      }

      // Restore setup lines
      _.each(this.quote.quote_lines, (line) => {
        let addon = _.find(this.addon_services, (addon) => addon.key == line.key);
        if (addon) {
          addon.selected = true;
          addon.description = line.description;
          addon.hours = line.quantity;
        }
      });

      this.integration_lines = [];
      this.purchases = [];
      this.service_lines = [];

      // Restore integrations and purchases
      _.each(this.quote.quote_lines, (line) => {
        if (line.key == 'carrier_integration' || line.key == 'order_channel_integration') {
          this.integration_lines.push(line);
        }
        if (line.key == 'product_purchase') {
          let purchase = {
            quantity: line.quantity,
            key: line.key,
            product: this.products.find((product) => product.id == line.product_id)
          };
          this.purchases.push(purchase);
        }
        if (line.key == 'service_purchase') {
          this.service_lines.push(line);
        }
      });

      // Restore one time discount
      let one_time_discount_line = _.find(this.quote.quote_lines, (line) => line.key == 'your_one_time_discount');
      if (one_time_discount_line && this.one_time_cost > 0) {
        this.one_time_discount_percent = Math.round(
          ((-1 * one_time_discount_line.unit_price) / this.one_time_cost) * 100
        );
      }
    }
  },
  watch: {
    'customer.default_locale': {
      handler: function () {
        this.set_addon_services();
      },
      immediate: true
    },
    calculated_lines: {
      handler() {
        if (!this.quote) {
          return;
        }

        if (!this.calculated_lines) {
          return;
        }

        this.quote.quote_lines = this.calculated_lines;
      },
      deep: true,
      immediate: true
    },
    plan_group() {
      if (!this.plan_group) {
        return;
      }

      if (this.plan_group[this.selected_billing_period]) {
        this.plan = this.plan_group[this.selected_billing_period];
      }
    },
    plan_groups() {
      if (!this.plan) {
        return;
      }
      // Set plan_group from existing quote
      this.plan_group = this.plan_groups[this.plan.name];
    },
    // Only allow updates to values in 0..100
    one_time_discount_percent(current, old) {
      if (!this.is_percentage(current)) {
        this.one_time_discount_percent = old;
        return;
      }
    },
    conditions_met() {
      if (this.conditions_met) {
        this.$emit('conditions_met', 'quote');
      } else {
        this.$emit('conditions_not_met', 'quote');
      }
    },
    currency() {
      this.reload_products();
    },
    unlimited_orders(new_unlimited_orders) {
      if (new_unlimited_orders) {
        this.quote.included_orders = null;
      }
    }
  },
  mounted: async function () {
    if (!this.customer_quote && this.current_billing_period) {
      this.selected_billing_period = this.current_billing_period;
    }

    let { data: products } = await Api.findAllPages('products');
    this.all_products = products;
    this.reload_products();

    if (!this.customer_quote) {
      return;
    }

    if (this.clone) {
      this.quote = _.cloneDeep(this.customer_quote);
    } else {
      this.quote = this.customer_quote;
    }
    this.restore_quote_details();
  }
};
</script>

<style scoped>
.row {
  padding-bottom: 20px;
}

.discount {
  color: mediumseagreen;
}

.danger {
  color: red;
}

.mrr-card {
  position: fixed;
  bottom: 0;
  z-index: 1;
  width: 500px;
  margin-left: 10%;
  box-shadow: 0 2px 15px 0 rgb(0 0 0 / 32%);
}
</style>
